import React from "react";
import { useIntl } from "react-intl";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import List from "../../components/list/List";
import Popup, { PopupContext } from "../../components/popup/Popup";

import { useDeviceGroupStore } from "../../store/device-group/DeviceGroupStore";

import { useDialog } from "../../components/dialog/useDialog";
import useErrorAlert from "../../hooks/useErrorAlert";
import useFormatter from "../../hooks/useFormatter";
import { useLoading } from "../../store/loading/LoadingContextStore";

import GroupInterface from "../../interfaces/GroupInterface";
import CustomError from "../../service/errors/custom.error";

import { MainPageOutletContext } from "../main/Main";
import "./device.scss";

function GroupList({
  groups,
  moveToGroup,
  createGroup,
}: {
  groups: GroupInterface[];
  moveToGroup: Function;
  createGroup: Function;
}) {
  const { formatMessage } = useIntl();
  const { formatElectricity } = useFormatter();
  const showErrorAlert = useErrorAlert();
  const { close } = React.useContext(PopupContext);

  return (
    <List
      list={[
        ...(groups.map(({ id, name, limit }) => ({
          title: `${name} | ${formatElectricity(limit)}`,
          onClick: async () => {
            try {
              if (await moveToGroup(id)) {
                close();
              }
            } catch (e: any) {
              console.error("Error when saving a device group", e);
              await showErrorAlert(
                e instanceof CustomError ? e.getStatusText() : undefined
              );
            }
          },
        })) as any),
        {
          title: formatMessage({ id: "device.move.new_group" }),
          onClick: () => createGroup(),
        },
      ]}
    />
  );
}

function DeviceMovePopup() {
  const navigate = useNavigate();
  const outlet = useOutletContext<MainPageOutletContext>();

  const { showConfirm } = useDialog();
  const { formatMessage } = useIntl();
  const processingAsyncFn = useLoading();

  const { groupStore, saveDeviceGroup, findDeviceById } = useDeviceGroupStore();

  const { deviceId, deviceType } = useParams();

  const device = React.useMemo(
    () => (deviceId ? findDeviceById(deviceId, deviceType) : undefined),
    [deviceId, deviceType, findDeviceById]
  );

  const moveToGroup = React.useCallback(
    async (groupId: string) => {
      if (device?.id == null || device?.type == null) return undefined;

      if (
        device?.groupId == null ||
        (await showConfirm({
          title: formatMessage({ id: "device.move.confirm_title" }),
          text: formatMessage({ id: "device.move.confirm_text" }),
          confirmLabel: formatMessage({ id: "device.move.confirm_yes" }),
        }))
      ) {
        await processingAsyncFn(() =>
          saveDeviceGroup(device.id, device.type, groupId)
        );
        return true;
      }

      return false;
    },
    [
      device?.groupId,
      device?.id,
      device?.type,
      formatMessage,
      processingAsyncFn,
      saveDeviceGroup,
      showConfirm,
    ]
  );

  const createGroup = React.useCallback(() => {
    if (device?.id == null) return undefined;

    navigate(outlet.generatePath("/group/create"), {
      state: { deviceId: device.id, deviceType: device.type },
      replace: true,
    });
  }, [device, navigate, outlet]);

  const groups = React.useMemo(
    () => groupStore.filter(({ id }) => id !== device?.groupId),
    [device, groupStore]
  );

  if (deviceId != null && device == null) return null;

  return (
    <Popup
      title={formatMessage({ id: "device.move.title" })}
      close={() => navigate(outlet.baseUrl)}
    >
      <GroupList
        groups={groups}
        moveToGroup={moveToGroup}
        createGroup={createGroup}
      />
    </Popup>
  );
}

export default DeviceMovePopup;
